// @flow
import type { State } from "@store/reducers";
import type { PaginationExtendedType } from "@types";
import type {
  CurrentMonitoringStage,
  MonitoringHistory,
} from "@modules/profile/reducer/my-absence-stage/types";
import type { NotificationContact } from "@modules/profile/types";
import type { MyDetails } from "@modules/profile/reducer/my-details/types";
import type {
  AdviceLibraryCategory,
  AdviceLibraryTopic,
} from "@modules/profile/reducer/advice-library/types";
import type { ActiveShiftPatternDay } from "@modules/profile/reducer/my-absences/types";
import type {
  IssueType,
  IssueFormData,
  CreatedTicket,
  TicketContent,
} from "@modules/profile/reducer/service-desk/types";
import { getPagination } from "@selectors";
import Ajv from "ajv";
import { removeNullAndUndefinedProps } from "@transformers/object";
import type { MyAbsenceDraft } from "@modules/profile/reducer/my-absences/types";

export type GetMyAbsenceMonitoringHistory = (state: State) => Array<MonitoringHistory>;
export type GetMyEmployees = (state: State) => Array<NotificationContact>;
export type GetMyEmployeesPage = (state: State) => number;

export type GetSelectedEmployeeId = (state: State) => ?number;
export type GetMyCurrentAbsenceMonitoringStage = (state: State) => CurrentMonitoringStage;

// Common
export const getError = (state: Object): ?string => (state.profile || {}).error;

export const getSuccessMessage = (state: Object): ?boolean => (state.profile || {}).success;

export const isLoading = (state: State): boolean => {
  const detailsLoading = (state.profile.myDetails || {}).requestsInProgress > 0;
  const absencesLoading = (state.profile.myAbsences || {}).requestsInProgress > 0;
  const employeesLoading = (state.profile.myEmployees || {}).requestsInProgress > 0;
  const advicesLoading = (state.profile.adviceLibrary || {}).requestsInProgress > 0;
  const serviceDirectoryLoading = (state.profile.serviceDirectory || {}).requestsInProgress > 0;
  const serviceDeskLoading = (state.profile.serviceDesk || {}).requestsInProgress > 0;

  return (
    detailsLoading ||
    absencesLoading ||
    employeesLoading ||
    advicesLoading ||
    serviceDirectoryLoading ||
    serviceDeskLoading
  );
};

// My Absences
export const getMyAbsences = (state: State): Object[] =>
  ((state.profile.myAbsences || {}).list || {}).content || [];

export const getMyAbsencesPagination = (state: State): PaginationExtendedType =>
  getPagination(state.profile.myAbsences.list);

export const showAddMyAbsencesModal = (state: State): boolean =>
  state.profile.myAbsences.modals.addAbsence.visible;

export const isEditMyAbsencesModal = (state: State): boolean =>
  state.profile.myAbsences.modals.addAbsence.editing;

export const showCloseMyAbsencesModal = (state: State): boolean =>
  state.profile.myAbsences.modals.closeAbsence.visible;

export const showReOpenMyAbsencesModal = (state: State): boolean =>
  state.profile.myAbsences.modals.reOpenAbsence.visible;

export const showCancelMyAbsencesModal = (state: State): boolean =>
  state.profile.myAbsences.modals.cancelAbsence.visible;

export const myAbsenceSchema = (state: State): Object | null =>
  state.profile.myAbsences.absencesSchema;

export const myClassificationsSchema = (state: State): Object | null =>
  state.profile.myAbsences.classificationsSchema;

export const myAbsenceDraft = (state: State): MyAbsenceDraft | null =>
  state.profile.myAbsences.draft;

export const myCurrentAbsence = (state: State): Object | null =>
  state.profile.myAbsences.currentAbsence;

export const myAbsenceActiveShiftPatternDays = (state: State) =>
  state.profile.myAbsences?.activeShiftPattern?.shiftPatternDays || [];

export const canOpenAbsence = (state: State) => state.profile.myAbsences.canOpenAbsence;

export const isMainDetailsValid = (state: State): boolean => {
  const schema = myAbsenceSchema(state);
  if (!schema) {
    return false;
  }
  const draft = myAbsenceDraft(state) || {};
  const validate = new Ajv().compile(schema);
  const isValid = validate(removeNullAndUndefinedProps(draft));

  return isValid;
};

export const isClassificationsValid = (state: State): boolean => {
  const schema = myClassificationsSchema(state);
  if (!schema) {
    return false;
  }
  const draft = myAbsenceDraft(state) || {};
  const validate = new Ajv().compile(schema);
  const isValid = validate(removeNullAndUndefinedProps(draft));

  return isValid;
};

export const isMainDetailClassificationsValid = (state: State): boolean => {
  return isMainDetailsValid(state) && isClassificationsValid(state);
};

export const myAbsenceStandardQuestion = (state: State) =>
  state.profile.myAbsences?.standardQuestions || [];

export const myAbsenceDynamicQuestion = (state: State) =>
  state.profile.myAbsences?.dynamicQuestions || [];

export const myAbsenceReferralQuestion = (state: State) =>
  state.profile.myAbsences?.referralQuestions || [];

///
////
////

// My Details
export const getMyDetails = (state: State): MyDetails | null =>
  state.profile.myDetails.data || null;

export const getContactInfoSchema = (state: State): Object | null =>
  state.profile.myDetails.contactInfoSchema || null;

export const getContactInfoModalVisible = (state: State): boolean =>
  state.profile.myDetails.modals.contactInfo.visible;

export const getHomePageSettingsModalVisible = (state: State): boolean =>
  state.profile.myDetails.modals.homePageSettings.visible;

export const getChangePasswordModalVisible = (state: State): boolean =>
  state.profile.myDetails.modals.changePassword.visible;

export const getAbsenceDetailsModalVisible = (state: State): boolean =>
  state.profile.myDetails.modals.absenceDetails.visible;

export const selectAbsenceStatisticsFilters = (state: State): Object =>
  state.form["absence-statistics-filters"].values || {};

// My Absence Stage
export const getMyAbsenceMonitoringHistory = (state: State): MonitoringHistory[] =>
  state.profile.myAbsenceStage.monitoringHistory || [];

export const getMyCurrentAbsenceMonitoringStage = (state: State): CurrentMonitoringStage | null =>
  state.profile.myAbsenceStage.current;

// Placeholder
export const someValue = () => null;

// My Employees
export const getMyEmployees = (state: State): Array<NotificationContact> =>
  state.profile.myEmployees.list.content.filter(
    itm => itm.notificationType === "FULL_NOTIFICATION",
  );

export const getMyEmployeesPagination = (state: State): PaginationExtendedType =>
  getPagination(state.profile.myEmployees.list);

export const getMyEmployeesPage = (state: State): ?number =>
  (getMyEmployeesPagination(state) || {}).pageNumber;

export const getSelectedEmployeeId = (state: State): ?number =>
  state.profile.myEmployees.selectedEmployeeId;

export const selectMyEmployees = (state: State): Array<NotificationContact> =>
  state.profile.myEmployees.list.content || [];

export const getAbsenceDetails = (state: State): ?Object => state.profile.myAbsences;

export const getAdviceLibraryCategories = (state: State): AdviceLibraryCategory[] =>
  state.profile.adviceLibrary.categories;

export const getAdviceLibraryTopic = (state: State): AdviceLibraryTopic | null =>
  state.profile.adviceLibrary.currentTopic;

export const getServiceDirectoryCategories = (state: State): AdviceLibraryCategory[] =>
  state.profile.serviceDirectory.categories;

export const getServiceDirectorySubCategories = (state: State): AdviceLibraryCategory[] =>
  state.profile.serviceDirectory.subCategories;

export const getServiceDirectoryTopics = (state: State): AdviceLibraryCategory[] =>
  state.profile.serviceDirectory.topics;

// Service Desk
export const getServiceDeskIssueTypes = (state: State): IssueType[] =>
  state.profile.serviceDesk.issueTypes;

export const getServiceDeskIssueFormData = (state: State): IssueFormData | null =>
  state.profile.serviceDesk.formData;

export const getServiceDeskCreatedTickets = (state: State): CreatedTicket[] =>
  state.profile.serviceDesk.createdTickets.content;

export const getServiceDeskTicketContent = (state: State): TicketContent | null =>
  state.profile.serviceDesk.ticketContent;

export const serviceDeskTicketsPagination = (state: State): PaginationExtendedType => {
  return getPagination(state.profile.serviceDesk.createdTickets);
};
